import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import {
  useMediaQuery,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import BuildIcon from "@mui/icons-material/Build";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SpeedIcon from "@mui/icons-material/Speed";

const services = [
  {
    title: "Smart Dispatch",
    description:
      "Automated and intelligent dispatching for logistics and field operations, ensuring speed and accuracy.",
    icon: SpeedIcon,
  },
  {
    title: "Reliable Support",
    description:
      "24/7 tech-enabled support systems that keep your business running smoothly and your customers satisfied.",
    icon: SupportAgentIcon,
  },
  {
    title: "Custom Solutions",
    description:
      "Tailored software and workflow automation designed to fit your unique operational needs.",
    icon: BuildIcon,
  },
];

const detailVariants = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 },
};

const Services = ({ currentSection }) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const SelectedIcon = services[selectedIndex].icon;

  return (
    <Box
      id="services"
      sx={{
        position: "relative",
        minHeight: "100vh", // Ensures full screen coverage
        backgroundImage: "url('https://www.shutterstock.com/image-photo/blurred-modern-office-soft-lighting-600nw-2591077855.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        py: 8,
        px: { xs: 2, sm: 4, md: 6 },
        overflow: "hidden", // Prevents content overflow
      }}
    >
      {/* Vignette Overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          background: `
            radial-gradient(
              ellipse at center,
              rgba(0,0,0,0.25) 0%,
              rgba(0,0,0,0.45) 60%,
              rgba(0,0,0,0.65) 100%
            )
          `,
        }}
      />

      {/* Content */}
      <Box sx={{ position: "relative", zIndex: 2 }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ color: "#fff", textShadow: "0 2px 12px #000" }}
          mt={5}
        >
          What We Offer
        </Typography>

        {isMobile ? (
          <Box sx={{ mt: 4 }}>
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <Paper
                  key={i}
                  elevation={6}
                  sx={{
                    minHeight: 200,
                    mb: 2,
                    p: 1,
                    background: "rgba(255,255,255,0.80)",
                    borderRadius: 4,
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                    textAlign: "center",
                  }}
                >
                  <Icon sx={{ fontSize: 50, color: "#1976d2" }} />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#333" }}
                  >
                    {service.title}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: "1rem", mt: 0 }}
                  >
                    {service.description}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: 4, mt: 4 }}>
            {/* Left List Panel */}
            <Paper
              elevation={4}
              sx={{
                width: 250,
                height: "60vh",
                background: "rgba(255,255,255,0.85)",
                borderRadius: 4,
                backdropFilter: "blur(6px)",
              }}
            >
              <List>
                {services.map((service, i) => (
                  <ListItemButton
                    key={i}
                    selected={selectedIndex === i}
                    onClick={() => setSelectedIndex(i)}
                  >
                    <ListItemText
                      primary={service.title}
                      primaryTypographyProps={{
                        fontWeight: selectedIndex === i ? 700 : 500,
                        color: selectedIndex === i ? "#1976d2" : "#333",
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>

            {/* Right Detail Panel */}
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedIndex}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={detailVariants}
                transition={{ duration: 0.4 }}
                style={{ flex: 1 }}
              >
                <Paper
                  elevation={6}
                  sx={{
                    p: 4,
                    height: "100%",
                    background: "rgba(255,255,255,0.9)",
                    borderRadius: 4,
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <SelectedIcon sx={{ fontSize: 80, color: "#1976d2" }} />
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, mt: 2, color: "#333" }}
                  >
                    {services[selectedIndex].title}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: "1.1rem", mt: 2 }}
                  >
                    {services[selectedIndex].description}
                  </Typography>
                </Paper>
              </motion.div>
            </AnimatePresence>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Services;
